import { Container } from 'typescript-ioc'
import { PageAnalysisFieldsService } from '@halo/services/page-analysis-fields.service'
import { PageCategoriesService } from '@pushly/aqe/lib/services'
import { AppService } from '@halo/services/app.service'
import { useEffect, useState } from 'react'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import EditFieldConfigStrategyDrawer, {
    IEditFieldConfigStrategyParams,
} from '@halo/components/edit-field-config-strategy-drawer/edit-field-config-strategy-drawer'
import { simpleNotification } from '@halo/_utils/notifications'
import { Button, Input, PageHeader, Well } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import React from 'react'
import { AutoComplete, Dropdown, Form, Menu, Modal, Select, Skeleton, Switch, Table } from 'antd'
import { CompoundTable, CompoundTableHeader } from '@halo/components/compound-table/compound-table'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { PageAnalysisFieldStrategyType } from '@pushly/aqe/lib/enums/page-analysis-field-strategy-type'
import TestPageAnalysisDrawer from '@halo/components/test-page-analysis-drawer/test-page-analysis-drawer'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { useForm } from 'antd/es/form/Form'
import { deepMerge } from '@halo/_utils/object'
import { EditOutlined } from '@ant-design/icons'
import {
    ADDITIONAL_FIELDS_EXPRESSION,
    IPageAnalysisField,
    IPageAnalysisFieldStrategy,
    STANDARD_PAGE_ANALYSIS_FIELDS,
} from '@pushly/aqe/lib/interfaces'
import { IPageCategory } from '@pushly/aqe/lib/interfaces'
import './style/edit-page-analysis-field.page.scss'

function EditPageAnalysisFieldPage(props: any) {
    const pageAnalysisConfigId = parseInt(props.match.params.pageAnalysisConfigId, 0)
    const fieldConfigId = parseInt(props.match.params.fieldConfigId, 0) || null

    const appService = Container.get(AppService)
    const pageAnalysisFieldsService: PageAnalysisFieldsService = Container.get(PageAnalysisFieldsService)
    const pageCategoriesService: PageCategoriesService = Container.get(PageCategoriesService)

    const [form] = useForm<IPageAnalysisField>()
    const [pageCategories, setPageCategories] = useState<IPageCategory[]>()
    const [pageAnalysisField, setPageAnalysisField] = useState<Partial<IPageAnalysisField>>()
    const [fieldStrategies, setFieldStrategies] = useState<IPageAnalysisFieldStrategy[]>()
    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [testHydratedPageAnalysis, setTestHydratedPageAnalysis] = useState<Partial<IHydratedPageAnalysisConfig>>()
    const [editFieldConfigStrategyParams, setEditFieldConfigStrategyParams] = useState<IEditFieldConfigStrategyParams>()

    useEffect(() => {
        const setFields = async () => {
            setIsLoading(true)

            const res = await Promise.all([fetchPageCategories(), fetchPageAnalysisField()])

            const [pc, paf] = res

            setPageCategories(pc)
            setPageAnalysisField(paf)
            setFieldStrategies(paf.strategies)
            form.setFieldsValue(paf)

            setIsLoading(false)
        }

        setFields()
    }, [pageAnalysisConfigId])

    async function fetchPageCategories() {
        return (await pageCategoriesService.fetchAll()).data
    }

    async function fetchPageAnalysisField(): Promise<Partial<IPageAnalysisField>> {
        let fc

        if (fieldConfigId) {
            const res = await pageAnalysisFieldsService.fetchById(pageAnalysisConfigId, fieldConfigId)
            fc = res.data
        } else {
            fc = {
                page_analysis_config_id: pageAnalysisConfigId,
                priority: 10,
            }
        }

        return fc
    }

    function testCurrentConfig() {
        setTestHydratedPageAnalysis({
            fields: [
                {
                    ...deepMerge(pageAnalysisField, form.getFieldsValue()),
                    strategies: fieldStrategies,
                },
            ],
        })
    }

    async function save() {
        setIsSaving(true)

        const saveDto = {
            ...deepMerge(pageAnalysisField, form.getFieldsValue()),
            strategies: fieldStrategies,
        }

        try {
            if (!fieldConfigId) {
                await pageAnalysisFieldsService.create(pageAnalysisConfigId, saveDto)
                simpleNotification('success', 'Page Analysis Config Field Created')
            } else {
                await pageAnalysisFieldsService.patch(pageAnalysisConfigId, fieldConfigId, saveDto)
                simpleNotification('success', 'Page Analysis Config Field Updated')
            }

            appService.route(`/page-analysis-configs/${pageAnalysisConfigId}#field-configs`)
        } catch (e) {
            simpleNotification('error', e.toString())
        }
        setIsSaving(false)
    }

    function renderRowActions(_: any, record: IPageAnalysisFieldStrategy, idx: number): React.ReactNode {
        return (
            <Dropdown.Button
                size="small"
                onClick={() => {
                    setEditFieldConfigStrategyParams({
                        fieldConfigStrategy: record,
                        ordinal: idx,
                    })
                }}
                overlay={
                    <Menu>
                        <Menu.Item onClick={() => del(idx)}>
                            <span>Delete</span>
                        </Menu.Item>
                    </Menu>
                }
            >
                <EditOutlined />
                <span>Edit</span>
            </Dropdown.Button>
        )
    }

    function del(idx: number) {
        Modal.confirm({
            title: 'Delete Category Strategy',
            content: (
                <>
                    <p>Are you sure you want to delete this field config strategy?</p>
                </>
            ),
            onOk: async (...args: any[]) => {
                const newFieldStrategies = fieldStrategies.slice(0)
                newFieldStrategies.splice(idx, 1)
                setFieldStrategies(newFieldStrategies)
            },
            onCancel: () => {},
        })
    }

    function render() {
        return (
            <>
                <div className={classnames('page', 'single-domain')}>
                    <div className={classnames('page-wrapper')}>
                        <PageHeader
                            title={fieldConfigId ? 'Edit Field Config' : 'Create Field Config'}
                            action={
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        testCurrentConfig()
                                    }}
                                    icon="caret-right"
                                    disabled={isSaving || isLoading}
                                >
                                    Test Field Config
                                </Button>
                            }
                            onTitleSet={noop}
                        />

                        <Well title="Details" className="type-3 nested page-analysis-field-details" hideFooter={true}>
                            <Skeleton loading={!pageAnalysisField} active={true} title={false}>
                                <Well mode="ghost" hideHeader={true} hideFooter={true}>
                                    {pageAnalysisField && (
                                        <>
                                            <Form
                                                form={form}
                                                onFinish={() => {
                                                    save()
                                                }}
                                            >
                                                <Form.Item
                                                    label="Field"
                                                    name="name"
                                                    rules={[
                                                        { required: true, message: 'Field is required' },
                                                        {
                                                            validator: (rule, value: string, next) => {
                                                                let matchFound = false

                                                                if (
                                                                    STANDARD_PAGE_ANALYSIS_FIELDS.indexOf(value) !== -1
                                                                ) {
                                                                    matchFound = true
                                                                } else if (ADDITIONAL_FIELDS_EXPRESSION.test(value)) {
                                                                    matchFound = true
                                                                }

                                                                if (!matchFound) {
                                                                    next(
                                                                        'Field name does not match any allowed field name ' +
                                                                            'or expression.',
                                                                    )
                                                                } else {
                                                                    next()
                                                                }
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <AutoComplete
                                                        options={STANDARD_PAGE_ANALYSIS_FIELDS.map((v) => {
                                                            return { value: v }
                                                        })}
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Data Type"
                                                    name="type"
                                                    rules={[{ required: true, message: 'Data Type is required' }]}
                                                >
                                                    <Select disabled={isSaving}>
                                                        <Select.Option key="string" value="string">
                                                            string
                                                        </Select.Option>
                                                        <Select.Option key="int" value="int">
                                                            int
                                                        </Select.Option>
                                                        <Select.Option key="float" value="float">
                                                            float
                                                        </Select.Option>
                                                        <Select.Option key="timestamp" value="timestamp">
                                                            timestamp
                                                        </Select.Option>
                                                        <Select.Option key="bool" value="bool">
                                                            bool
                                                        </Select.Option>
                                                        <Select.Option key="url" value="url">
                                                            url
                                                        </Select.Option>
                                                        <Select.Option key="list[string]" value="list[string]">
                                                            list[string]
                                                        </Select.Option>
                                                        <Select.Option key="list[int]" value="list[int]">
                                                            list[int]
                                                        </Select.Option>
                                                        <Select.Option key="list[float]" value="list[float]">
                                                            list[float]
                                                        </Select.Option>
                                                        <Select.Option key="list[timestamp]" value="list[timestamp]">
                                                            list[timestamp]
                                                        </Select.Option>
                                                        <Select.Option key="list[bool]" value="list[bool]">
                                                            list[bool]
                                                        </Select.Option>
                                                        <Select.Option key="list[url]" value="list[url]">
                                                            list[url]
                                                        </Select.Option>
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item
                                                    label="Priority"
                                                    name="priority"
                                                    rules={[{ required: true, message: 'Priority is required' }]}
                                                >
                                                    <Input disabled={isSaving} type="number" autoComplete="off" />
                                                </Form.Item>
                                            </Form>
                                        </>
                                    )}
                                </Well>
                            </Skeleton>
                        </Well>

                        <Well
                            title="Strategies"
                            className="type-2"
                            loading={isLoading}
                            hideHeader={true}
                            disableSubmit={isSaving || isLoading}
                            submitText={fieldConfigId ? 'Update' : 'Create'}
                            onSubmit={() => {
                                form.submit()
                            }}
                            onCancel={() => {
                                appService.route(`/page-analysis-configs/${pageAnalysisConfigId}#field-configs`)
                            }}
                        >
                            {pageAnalysisField && (
                                <CompoundTable>
                                    <CompoundTableHeader
                                        right={
                                            <>
                                                <Button
                                                    type="primary"
                                                    disabled={isSaving || isLoading}
                                                    onClick={() => {
                                                        setEditFieldConfigStrategyParams({
                                                            fieldConfigStrategy: {
                                                                config: {},
                                                            },
                                                        })
                                                    }}
                                                >
                                                    <span>Add Field Config Strategy</span>
                                                </Button>
                                            </>
                                        }
                                    />
                                    <Table<IPageAnalysisFieldStrategy>
                                        size="small"
                                        pagination={false}
                                        dataSource={fieldStrategies}
                                        locale={{
                                            emptyText: (
                                                <AntdTableEmptyPlaceholder
                                                    text="No Page Analysis Configs"
                                                    icon="file-search"
                                                />
                                            ),
                                        }}
                                    >
                                        <Table.Column className="type" title="Type" dataIndex="type" width={80} />
                                        <Table.Column
                                            className="details"
                                            title="Details"
                                            render={(text, record: IPageAnalysisFieldStrategy, idx) => {
                                                return (
                                                    <>
                                                        {record.type === PageAnalysisFieldStrategyType.XPATH && (
                                                            <span>{record.config.xpath}</span>
                                                        )}

                                                        {record.type === PageAnalysisFieldStrategyType.JSONLD && (
                                                            <span>
                                                                {record.config.schema_type} &gt;{' '}
                                                                {record.config.json_path}
                                                            </span>
                                                        )}

                                                        {record.type === PageAnalysisFieldStrategyType.JSONPATH && (
                                                            <span>
                                                                {record.config.element_id} &gt;{' '}
                                                                {record.config.json_path}
                                                            </span>
                                                        )}
                                                    </>
                                                )
                                            }}
                                        />
                                        <Table.Column
                                            className="actions"
                                            title="Actions"
                                            width={180}
                                            render={renderRowActions}
                                        />
                                    </Table>
                                </CompoundTable>
                            )}
                        </Well>
                    </div>
                </div>

                <EditFieldConfigStrategyDrawer
                    params={editFieldConfigStrategyParams}
                    onClose={() => setEditFieldConfigStrategyParams(null)}
                    onUpdate={(fieldConfigStrategy, ordinal) => {
                        const newStrategies = (fieldStrategies || []).slice(0)

                        if (ordinal != null) {
                            newStrategies[ordinal] = fieldConfigStrategy
                        } else {
                            newStrategies.push(fieldConfigStrategy)
                        }

                        setFieldStrategies(newStrategies)
                        setEditFieldConfigStrategyParams(null)
                    }}
                />

                <TestPageAnalysisDrawer
                    title="Test Field Config"
                    isPartial={true}
                    hydratedPageAnalysis={testHydratedPageAnalysis}
                    onClose={() => setTestHydratedPageAnalysis(null)}
                    pageCategories={pageCategories}
                />
            </>
        )
    }

    return render()
}

export default EditPageAnalysisFieldPage
